import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import {
  KEY_RECOIL,
  REVIEWED_BEAT_FILTER,
  SORT_BEATS_OPTIONS,
  TYPE_BEAT_ENUM,
} from '@/shared/config';
import { LOAD_BEAT_STATUS } from '@/shared/model';
import {
  BEAT_HR_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectActiveTab,
  selectBeatChanges,
  selectBulkBeatsChanges,
  selectBulkSwapBeats,
  selectControlModal,
  selectResetBulkBeatsChanges,
  selectIsLoadingStrip,
  selectResetSwapBeats,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  selectSwapBeatChanges,
  useReportStore,
} from '@/entities/report';
import { findInBulkChanges } from '@/entities/beat';
import { handleUpdateBeatReview, hasTemplateBeat } from './reviewBeatsHelper';
import { handleChangeTypeMultipleBeats } from './rrInterval/handler';

/**
 * Drives the beat action bar of the beats and heart-rate tab, deciding which type, review, swap, reset, filter and sort controls are enabled and wiring their handlers into the report store. Reads tab-1 state via keyed selectors, merges the single selected beat with its bulk selection and overlays pending type, review and template edits onto it, derives the disabled and reset-visible flags from that merge, and registers a window keydown listener that maps N, S, V, Q, D and R onto the same handlers while the beats tab is hovered.
 *
 * @returns The beats action-bar state together with the type change, review, reset, swap, filter and sort handlers.
 */
const useRrHeatmapButtonTypes = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the pending beat type edits for the beats tab. Forwards the value to the store action with the tab key bound, so callers pass either a new list or an updater.
   *
   * @param v - New beat change list, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);

  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const loadingBeatStatus = useReportStore((s) => s.loadingBeatStatus);
  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil));
  const resetBulkBeatsChanges = useReportStore(selectResetBulkBeatsChanges(keyRecoil));
  /**
   * Reads the list of bulk beat changes that have been reverted for the beats tab. Applies the tab-keyed reset selector to the current report store snapshot instead of subscribing.
   *
   * @returns The current reset entries for bulk beat changes.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the bulk beat type edits queued for the beats tab from the current report store snapshot. Applies the tab-keyed selector to the store state imperatively so handlers see values committed earlier in the same event.
   *
   * @returns The current bulk beat changes for the tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the list of bulk beat changes that have been reverted for the beats tab. Forwards the value to the store action with the tab key bound.
   *
   * @param v - New reset entries for bulk beat changes, or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beats tab options record that mirrors the pending beat, reset, swap and review collections for undo and redo. Forwards the value to the store action with the tab key bound.
   *
   * @param v - New beat options object, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  /**
   * Reads the beats tab modal visibility flags from the report store snapshot. Applies the tab-keyed control-modal selector imperatively, so a handler can branch on flags it may have just written.
   *
   * @returns The current control-modal flag set for the tab.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab modal visibility flags. Forwards the value to the store action with the tab key bound, so callers pass either a full flag set or an updater.
   *
   * @param v - New control-modal flag set, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);

  /**
   * Reads the pending per-beat template swap edits for the beats tab. Applies the tab-keyed selector to the current report store snapshot rather than subscribing to it.
   *
   * @returns The current swap-beat changes for the tab.
   */
  const getSwapBeatChanges = () => selectSwapBeatChanges(keyRecoil)(useReportStore.getState());
  const swapBeatChanges = useReportStore(selectSwapBeatChanges(keyRecoil));
  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Writes the pending per-beat template swap edits for the beats tab. Forwards the value to the store action with the tab key bound.
   *
   * @param v - New swap-beat change list, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(keyRecoil, v);
  const bulkSwapBeats = useReportStore(selectBulkSwapBeats(keyRecoil));
  const resetSwapBeats = useReportStore(selectResetSwapBeats(keyRecoil));
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Writes the reverted bulk template swap entries for the beats tab. Forwards the value to the store action with the tab key bound.
   *
   * @param v - New reset entries for swapped beats, or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(keyRecoil, v);

  const isShowBeatAction = loadingBeatStatus.status !== LOAD_BEAT_STATUS.NONE;
  const isShowSort = isShowBeatAction && hasTemplateBeat(activeButton);
  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads/writes beat-only fields (id, originalData, ...) that IEvent/IEcgBookmark lack - pre-existing gap.
  const selectedBeatThumbnail = useReportStore(selectSelectedStrip(keyRecoil));

  const visibleReviewedFilter = useReportStore((s) => s.visibleReviewedFilter);
  const setVisibleReviewedFilter = useReportStore((s) => s.actions.setVisibleReviewedFilter);
  const beatsSortType = useReportStore((s) => s.beatsSortType);
  const setBeatsSortType = useReportStore((s) => s.actions.setBeatsSortType);
  /**
   * Reads the pending beat type edits for the beats tab straight out of the report store rather than through a subscription. Applies the tab-scoped selector to the current store snapshot so callers see the latest edits without waiting for a React re-render.
   *
   * @returns The current list of pending beat changes for the tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the bulk template swap entries queued for the beats tab from the report store. Applies the tab-keyed selector to the current snapshot so handlers act on freshly written values.
   *
   * @returns The current bulk swap-beat entries for the tab.
   */
  const getBulkSwapBeats = () => selectBulkSwapBeats(keyRecoil)(useReportStore.getState());
  /**
   * Reads the reverted bulk template swap entries for the beats tab. Applies the tab-keyed reset selector to the current report store snapshot.
   *
   * @returns The current reset entries for swapped beats.
   */
  const getResetSwapBeats = () => selectResetSwapBeats(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending reviewed or unreviewed flags for individual beats from the report store. Pulls the field off the store snapshot directly so handlers see edits made in the same tick.
   *
   * @returns The current per-beat review changes.
   */
  const getReviewedBeatsChanges = () => useReportStore.getState().reviewedBeatsChanges;
  /**
   * Reads the bulk reviewed-beat flags held in the report store. Pulls the field directly off the store snapshot, bypassing the subscription used for rendering.
   *
   * @returns The current bulk reviewed-beat entries.
   */
  const getBulkReviewedBeats = () => useReportStore.getState().bulkReviewedBeats;
  /**
   * Reads the reverted bulk reviewed-beat entries from the report store. Pulls the field off the store snapshot directly rather than through a selector subscription.
   *
   * @returns The current reset entries for bulk reviewed beats.
   */
  const getResetBulkReviewedBeats = () => useReportStore.getState().resetBulkReviewedBeats;

  const selectedStrip: any = useMemo(() => {
    const foundBeatChange = _.find(
      beatChanges,
      (x) => x?.id === (selectedBeatThumbnail as any)?.id,
    );
    const foundReviewedChange = _.find(
      reviewedBeatsChanges,
      (x) => x?.id === (selectedBeatThumbnail as any)?.id,
    );
    const foundSwapBeatChange = _.find(
      swapBeatChanges,
      (x) => x?.id === (selectedBeatThumbnail as any)?.id,
    );
    const cloneBeat: any = {
      ...(selectedBeatThumbnail || {}),
      ...((selectedBeatThumbnail as any)?.originalData || {}),
    };
    if (foundBeatChange) {
      _.assign(cloneBeat, { type: foundBeatChange.type });
    }
    if (foundReviewedChange) {
      _.assign(cloneBeat, { isReviewed: foundReviewedChange.isReviewed });
    }
    if (foundSwapBeatChange) {
      _.assign(cloneBeat, { templateId: foundSwapBeatChange.templateId });
    }
    return cloneBeat;
  }, [selectedBeatThumbnail, beatChanges, reviewedBeatsChanges, swapBeatChanges]);

  const mergeSelectedStrip = _.filter(
    _.uniqBy([selectedStrip || {}, ...(selectedMultipleStrip || [])], 'id'),
    (x) => !_.isEmpty(x),
  );

  const foundHasChanged = _.find(beatChanges, (x) => x?.id === selectedStrip?.id);
  const isDisableWhenSaving = foundHasChanged?.isSaving;
  const isNotSelectedStrip = _.isEmpty(mergeSelectedStrip) && !selectedStrip?.id;

  const isDisableButton =
    isNotSelectedStrip ||
    isDisableWhenSaving ||
    loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE ||
    isLoadingStrip;
  const isDisableCommonButton = isDisableButton || isDisableWhenSaving;

  const isDisableChangingButton = isDisableButton || controlModal.isShowChangeDurationButtonType;
  const isShowResetButton = useMemo(() => {
    if (foundHasChanged) return true;
    if (mergeSelectedStrip.length > 0) {
      let isChangedBulk = false;
      _.forEach(mergeSelectedStrip, (strip: any) => {
        const blulkCell = findInBulkChanges(bulkBeatsChanges, strip);
        if (blulkCell) {
          const foundAppliedBulk = _.find(resetBulkBeatsChanges, (x) => x?.id === strip?.id);
          if (!foundAppliedBulk) {
            isChangedBulk = true;
            return false;
          }
        }
        const reviewedBulk = findInBulkChanges(bulkReviewedBeats, strip);
        if (reviewedBulk) {
          if (
            reviewedBulk.isReviewed !== strip.originalReviewed &&
            !_.find(resetBulkReviewedBeats, (x) => x.id === strip.id)
          ) {
            isChangedBulk = true;
            return false;
          }
        }
        const inbulkSwapBeats = findInBulkChanges(bulkSwapBeats, strip);
        if (inbulkSwapBeats) {
          if (
            inbulkSwapBeats.templateId !== strip.originalTemplateId &&
            !_.find(resetSwapBeats, (x) => x.id === strip.id)
          ) {
            isChangedBulk = true;
            return false;
          }
        }
      });
      return (
        isChangedBulk ||
        !!mergeSelectedStrip.find((strip: any) => {
          const foundChange = _.find(beatChanges, (x) => x?.id === strip?.id);
          const foundReviewedChange = _.find(reviewedBeatsChanges, (x) => x?.id === strip?.id);
          const foundSwapBeatChange = _.find(swapBeatChanges, (x) => x?.id === strip?.id);
          if (foundChange || foundReviewedChange || foundSwapBeatChange) {
            return true;
          }
          return false;
        })
      );
    }
    return false;
  }, [
    foundHasChanged,
    beatChanges,
    mergeSelectedStrip,
    bulkBeatsChanges,
    reviewedBeatsChanges,
    resetBulkReviewedBeats,
    swapBeatChanges,
  ]);

  /**
   * Applies a beat type such as normal, supraventricular, ventricular, unknown or deleted across the currently selected beats, or computes the reverted values without writing them. Reads the pending, bulk and reset collections off the store snapshot, hands them to the multi-beat type helper, and either returns the recomputed lists when reverting or writes beat changes, reset bulk changes, swap changes and the mirrored beat options back to the store.
   *
   * @param type - Beat type to apply to the selected beats; ignored when reverting.
   * @param isReset - When true, computes the reverted collections and returns them instead of committing to the store.
   * @returns The recomputed beat, reset-bulk and swap collections when reverting, otherwise an empty object.
   */
  const handleChangeStripType = (type: any, isReset?: boolean) => {
    const { newBeatChanges, newResetBulksBeatChanges, newSwapBeatChanges, newResetBulkSwapBeats } =
      handleChangeTypeMultipleBeats({
        selectedBeats: mergeSelectedStrip,
        bulkBeatsChanges: getBulkBeatsChanges(),
        beatChanges: getBeatChanges(),
        resetBulksBeatChanges: getResetBulkBeatsChanges(),
        bulkSwapBeats: getBulkSwapBeats(),
        swapBeatChanges: getSwapBeatChanges(),
        resetBulkSwapBeats: getResetSwapBeats(),
        type,
        activeButton,
        isReset,
      });

    if (isReset) {
      return {
        stripChangesUpdate: newBeatChanges,
        resetBulkChanges: newResetBulksBeatChanges,
        cloneSwapBeatChanges: newSwapBeatChanges,
        resetSwapBeats: newResetBulkSwapBeats,
      };
    }

    // A single set only: calling setBeatChanges twice made the second call's persist see
    // prev === next (equal), and the shared 300ms debounce keeps only the last call's args, so it
    // skipped the upsert and beatChangesState was never written — the bulk edit was then dropped on
    // reload (checkHasChangedSavedIndexDB sees no beatChangesState and clears the store).
    setBeatChanges((prev: any) => {
      if (_.isEqual(prev, newBeatChanges)) {
        return prev;
      }
      return newBeatChanges;
    });
    setResetBulkBeatsChanges(newResetBulksBeatChanges);
    setSwapBeatChanges(newSwapBeatChanges);
    setResetSwapBeats(newResetBulkSwapBeats);
    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: newBeatChanges,
      resetBulkBeatsChangesState: newResetBulksBeatChanges,
      swapBeatChangesState: newSwapBeatChanges,
      resetSwapBeatsState: newResetBulkSwapBeats,
    }));
    return {};
  };

  const handleChangeBeatReview = ({
    isReviewed,
    isReset,
  }: {
    isReviewed?: boolean;
    isReset?: boolean;
  }) => {
    const { beatReviewChanges, bulkReviewResetBeats } = handleUpdateBeatReview({
      beatChanges: getReviewedBeatsChanges(),
      bulkReviewedBeats: getBulkReviewedBeats(),
      resetBulkReviewedBeats: getResetBulkReviewedBeats(),
      strips: mergeSelectedStrip,
      isReset,
      isReviewed,
      activeButton,
    });
    if (isReset) {
      return {
        beatReviewChanges,
        bulkReviewResetBeats,
      };
    }
    setReviewedBeatsChanges(beatReviewChanges);
    setResetBulkReviewedBeats(bulkReviewResetBeats);
    setBeatOptions((prev: any) => ({
      ...prev,
      reviewedBeatsChangesState: beatReviewChanges,
      resetBulkReviewedBeatsState: bulkReviewResetBeats,
    }));
    return {};
  };

  /**
   * Reverts every pending edit on the selected beats, covering type changes, review flags and template swaps. Runs the type and review handlers in reset mode to compute the restored collections, then writes beat changes, reset bulk changes, swap changes, reset swap beats, review changes and the mirrored beat options back to the store.
   *
   * @returns Nothing.
   */
  const onClickResetBeat = () => {
    const { stripChangesUpdate, resetBulkChanges, cloneSwapBeatChanges, resetSwapBeats } =
      handleChangeStripType(undefined, true);
    const { beatReviewChanges, bulkReviewResetBeats } = handleChangeBeatReview({ isReset: true });
    setBeatChanges((prev: any) => {
      if (_.isEqual(prev, stripChangesUpdate)) {
        return prev;
      }
      return stripChangesUpdate;
    });
    setResetBulkBeatsChanges(resetBulkChanges);
    setSwapBeatChanges(cloneSwapBeatChanges);
    setResetSwapBeats(resetSwapBeats);
    setReviewedBeatsChanges(beatReviewChanges);
    setResetBulkReviewedBeats(bulkReviewResetBeats);

    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: stripChangesUpdate,
      resetBulkBeatsChangesState: resetBulkChanges,
      swapBeatChangesState: cloneSwapBeatChanges,
      resetSwapBeatsState: resetSwapBeats,
      reviewedBeatsChangesState: beatReviewChanges,
      resetBulkReviewedBeatsState: bulkReviewResetBeats,
    }));
  };

  /**
   * Opens the template swap dialog for the selected beats, but only for beat types that carry a template. Reads the current modal flags and, when a swap-all dialog is already open, closes everything first and reopens the single swap dialog on a short timer so the modal remounts cleanly.
   *
   * @returns Nothing.
   */
  const handleClickSwapBeats = () => {
    if (!hasTemplateBeat(activeButton)) return;
    const controlModal: any = getControlModal();
    if (controlModal.isShowSwapAllTemplateById || controlModal.isShowSwapAllTemplateByPage) {
      setControlModal(DEFAULT_CONTROL_MODAL_VALUE);
      setTimeout(() => {
        setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowSwapTemplate: true } as any);
      }, 100);
    } else {
      setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowSwapTemplate: true } as any);
    }
  };

  /**
   * Applies the reviewed-beat visibility filter chosen from the filter menu. Strips the filter- prefix off the menu key, parses the remainder as an index into the reviewed-beat filter options, and stores the matching option.
   *
   * @param key - Menu item key of the chosen filter, prefixed with filter-.
   * @returns Nothing.
   */
  const onChangeFilter = ({ key }: { key: string }) => {
    const filter = key.replace('filter-', '');
    setVisibleReviewedFilter(REVIEWED_BEAT_FILTER[parseInt(filter, 10)]);
  };

  /**
   * Applies the beat sort order chosen from the sort menu. Strips the sort- prefix off the menu key, parses the remainder as an index into the beat sort options, and stores the matching option.
   *
   * @param key - Menu item key of the chosen sort order, prefixed with sort-.
   * @returns Nothing.
   */
  const onChangeSort = ({ key }: { key: string }) => {
    const sort = key.replace('sort-', '');
    setBeatsSortType(SORT_BEATS_OPTIONS[parseInt(sort, 10)]);
  };

  const isBeatsReviewed = useMemo(() => {
    if (loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE) return false;
    if (mergeSelectedStrip.length > 0) {
      let reviewedCount = 0;
      _.forEach(mergeSelectedStrip, (x) => {
        const ele = reviewedBeatsChanges.find((d: any) => d.id === x.id);
        if (ele) {
          if (ele.isReviewed) {
            reviewedCount += 1;
          }
        } else {
          const reviewInbulk = findInBulkChanges(bulkReviewedBeats, x);
          if (reviewInbulk) {
            const isReset = _.find(resetBulkReviewedBeats, (x) => x.id === selectedStrip.id);
            if (isReset) {
              if (x.originalReviewed) {
                reviewedCount += 1;
              }
            } else if (reviewInbulk.isReviewed) {
              reviewedCount += 1;
            }
          } else if (x.isReviewed) {
            reviewedCount += 1;
          }
        }
      });
      return reviewedCount === mergeSelectedStrip?.length;
    }
    return false;
  }, [
    selectedMultipleStrip,
    selectedStrip,
    reviewedBeatsChanges,
    mergeSelectedStrip,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    loadingBeatStatus,
  ]);

  useEffect(() => {
    /**
     * Maps single-key presses onto beat type and review actions while the pointer is over the beats and heart-rate tab. Ignores auto-repeat, strip loading, presses outside the hovered tab zone and any modifier combination, then routes N, S, V and Q to the matching beat type, D to delete and R to toggling the review flag, each behind its own disabled check.
     *
     * @param event - Keyboard event from the window keydown listener.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 78: {
          // N
          if (!isDisableButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.N);
          }
          break;
        }
        case 83: {
          // S
          if (!isDisableButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.S);
          }
          break;
        }
        case 86: {
          // V
          if (!isDisableButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.V);
          }
          break;
        }
        case 81: {
          // Q
          if (!isDisableButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.Q);
          }
          break;
        }
        case 68: {
          // Delete (D)
          if (!isDisableChangingButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.DELETED);
          }
          break;
        }

        case 82: {
          // R
          if (!isDisableButton) {
            handleChangeBeatReview({ isReviewed: !isBeatsReviewed });
          }
          break;
        }

        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedMultipleStrip,
    selectedStrip,
    beatChanges,
    isLoadingStrip,
    isDisableChangingButton,
    isBeatsReviewed,
  ]);

  return {
    keyRecoil,
    activeButton,
    isLoadingStrip,
    isDisableButton,
    isDisableChangingButton,
    isDisableCommonButton,
    isShowResetButton,
    isShowBeatAction,
    isShowSort,
    isBeatsReviewed,
    visibleReviewedFilter,
    beatsSortType,
    handleChangeStripType,
    handleChangeBeatReview,
    onClickResetBeat,
    handleClickSwapBeats,
    onChangeFilter,
    onChangeSort,
  };
};

export default useRrHeatmapButtonTypes;
